import { Component, Inject, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Subscription } from 'rxjs';
import { EnvService, ImportantNoticeConfig, ImportantNoticeMessage } from '../../services/env.service';
import { LanguageService } from '../../services/language.service';
import { AuthService } from '../../services/auth.service';
import { ActiveToast, ToastrService } from 'ngx-toastr';

const DISMISSED_STORAGE_KEY = 'dmDismissed';
const DISMISS_TTL_DAYS = 90;

@Component({
  selector: 'app-important-notice',
  standalone: true,
  template: ``
})
export class ImportantNoticeComponent implements OnInit, OnDestroy {
  private noticeConfig: ImportantNoticeConfig | null = null;
  private noticeEnabledSwitch = true;
  private subscription = new Subscription();
  private activeToast: ActiveToast<any> | null = null;
  private suppressedToastIds = new Set<number>();

  constructor(
    private envService: EnvService,
    private languageService: LanguageService,
    private authService: AuthService,
    private toastr: ToastrService,
    @Inject(PLATFORM_ID) private platformId: object
  ) {}

  ngOnInit(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    const envConfig = this.envService.config;
    this.noticeEnabledSwitch = !!envConfig.ImportantNoticeEnabled;
    this.noticeConfig = (envConfig as any).importantNotice ?? null;
    this.refreshFromConfig();

    this.subscription.add(
      this.languageService.currentLanguage$.subscribe(() => {
        this.refreshFromConfig();
      })
    );
  }

  private refreshFromConfig(): void {
    if (!this.noticeEnabledSwitch || !this.noticeConfig || !this.noticeConfig.enabled) {
      this.clearToast();
      return;
    }

    if (!this.isWithinValidity(this.noticeConfig)) {
      this.clearToast();
      return;
    }

    if (this.isDismissed()) {
      this.clearToast();
      return;
    }

    const message = this.resolveMessage();
    if (!message) {
      this.clearToast();
      return;
    }
    this.showToast(message.html);
  }

  private resolveMessage(): ImportantNoticeMessage | null {
    const messages = this.noticeConfig?.messages ?? [];
    if (!messages.length) return null;

    const userLang = this.authService.currentUserValue?.language_locale;
    const lang = this.normalizeLanguageCode(userLang || this.languageService.currentLanguage);
    const enriched = messages.map((item: any) => ({
      original: item as ImportantNoticeMessage,
      lang: this.normalizeLanguageCode(item?.lang ?? item?.language ?? item?.locale)
    }));

    return (
      enriched.find(item => item.lang === lang)?.original ||
      enriched.find(item => item.lang.startsWith(`${lang}-`))?.original ||
      enriched.find(item => item.lang === 'en')?.original ||
      enriched.find(item => item.lang.startsWith('en-'))?.original ||
      messages[0] ||
      null
    );
  }

  private normalizeLanguageCode(lang: string | null | undefined): string {
    return (lang || '').trim().toLowerCase();
  }

  private isWithinValidity(config: ImportantNoticeConfig): boolean {
    const now = Date.now();
    const from = config.validFrom ? Date.parse(config.validFrom) : null;
    const to = config.validTo ? Date.parse(config.validTo) : null;

    if (from && Number.isNaN(from)) return false;
    if (to && Number.isNaN(to)) return false;
    if (from && now < from) return false;
    if (to && now > to) return false;
    return true;
  }

  private dismissedKey(): string {
    const id = this.noticeConfig?.id?.trim();
    return id ? `${DISMISSED_STORAGE_KEY}:${id}` : DISMISSED_STORAGE_KEY;
  }

  private isDismissed(): boolean {
    const raw = localStorage.getItem(this.dismissedKey());
    if (!raw) return false;
    if (raw === '1') return true; // legacy format
    const expiresAt = Number(raw);
    if (!Number.isFinite(expiresAt)) return false;
    return Date.now() < expiresAt;
  }

  private showToast(htmlMessage: string): void {
    this.clearToast();
    this.activeToast = this.toastr.info(this.normalizeMessageHtml(htmlMessage), undefined, {
      enableHtml: true,
      timeOut: 0,
      disableTimeOut: true,
      closeButton: true,
      toastClass: 'ngx-toastr important-notice-toast',
      positionClass: 'toast-top-center',
      tapToDismiss: false,
      newestOnTop: true
    });
    const toastId = this.activeToast.toastId;

    this.subscription.add(
      this.activeToast.onHidden.subscribe(() => {
        const wasSuppressed = this.suppressedToastIds.delete(toastId);
        if (!wasSuppressed) {
          this.markDismissed();
        }
        // Hidden events for previously-cleared toasts can fire after a new toast is shown.
        // Only clear tracking when the hidden toast is still the currently active one.
        if (this.activeToast?.toastId === toastId) {
          this.activeToast = null;
        }
      })
    );
  }

  private clearToast(): void {
    if (!this.activeToast) {
      return;
    }
    this.suppressedToastIds.add(this.activeToast.toastId);
    this.toastr.clear(this.activeToast.toastId);
    this.activeToast = null;
  }

  private markDismissed(): void {
    const expiresAt = Date.now() + DISMISS_TTL_DAYS * 24 * 60 * 60 * 1000;
    localStorage.setItem(this.dismissedKey(), String(expiresAt));
  }

  private normalizeMessageHtml(rawHtml: string): string {
    return rawHtml
      .replace(/<img\b[^>]*>/gi, '')
      .replace(/\s{2,}/g, ' ')
      .trim();
  }

  ngOnDestroy(): void {
    this.clearToast();
    this.subscription.unsubscribe();
  }
}
